// Il "percorso di crescita" su eDive: cosa sblocca ogni ruolo/traguardo, cosa serve
// per arrivarci, e come. Usato per comunicare i requisiti in chiave di crescita e
// comunità (mai come muro), coerente col principio di prodotto empatia/crescita.
//
// Ogni voce è pensata per far sentire l'utente ACCOMPAGNATO verso il prossimo passo,
// non gestito o limitato.

import type { ProfileType } from '@/types'
import type { AllowedRole } from '@/api/profile'

export type UnlockAction =
  | { kind: 'request-role'; role: AllowedRole } // apre RequestRoleWizard
  | { kind: 'add-certification' } // apre AddCertificationWizard
  | { kind: 'complete-profile' } // manda al profilo
  | { kind: 'become-center' } // onboarding dive center
  | { kind: 'set-profile-type'; type: ProfileType } // self-service: attiva subito il profileType (no approvazione)
  | { kind: 'link'; to: string } // link generico

export interface RoleJourney {
  /** profileType o ruolo target */
  key: ProfileType
  icon: string
  /** Titolo aspirazionale, sul "tu" */
  title: string
  /** Frase calda: dove può arrivare l'utente */
  headline: string
  /** Cosa sblocca — benefici concreti in chiave crescita/comunità */
  unlocks: string[]
  /** Cosa serve per arrivarci (requisiti chiari, non punitivi) */
  requirements: string[]
  /** Come fare il prossimo passo */
  primaryAction: { label: string; action: UnlockAction }
  secondaryAction?: { label: string; action: UnlockAction }
}

export const ROLE_JOURNEYS: Partial<Record<ProfileType, RoleJourney>> = {
  DIVER: {
    key: 'DIVER',
    icon: '🤿',
    title: 'Il percorso da Subacqueo',
    headline: 'Il mare ti aspetta: registra le tue immersioni e cresci come subacqueo con eDive.',
    unlocks: [
      'Tieni il tuo logbook digitale: profondità, tempi, siti e compagni',
      'Pianifica le immersioni e riconcilia i piani col logbook',
      'Allenati a secco e resta sempre pronto: respiro, skill, emergenze e prontezza',
      'Segui i tuoi progressi e il percorso brevetti',
      'Connettiti con altri subacquei e trova buddy',
    ],
    requirements: [
      'Nessun requisito: inizia subito',
      'Aggiungi i tuoi brevetti quando vuoi',
    ],
    primaryAction: { label: 'Attiva il profilo Subacqueo', action: { kind: 'set-profile-type', type: 'DIVER' } },
    secondaryAction: { label: 'Aggiungi un brevetto', action: { kind: 'add-certification' } },
  },
  FREEDIVER: {
    key: 'FREEDIVER',
    icon: '🌀',
    title: 'Il percorso da Apneista',
    headline: 'Un respiro, la discesa, il silenzio: fai crescere la tua apnea con eDive.',
    unlocks: [
      'Allena respirazione, tolleranza a CO₂/O₂ e concentrazione ogni giorno',
      'Cronometra le tue statiche e supera i tuoi record, in sicurezza',
      'Segui profondità, tempi e progressi nel tempo',
      'Connettiti con altri apneisti e trova compagni di allenamento',
    ],
    requirements: [
      'Bastano pochi minuti al giorno, comodamente a secco',
      'Aggiungi i tuoi eventuali brevetti freediving',
    ],
    primaryAction: { label: 'Allenati adesso', action: { kind: 'link', to: '/app/apnea' } },
    secondaryAction: { label: 'Aggiungi un brevetto', action: { kind: 'add-certification' } },
  },
  DIVEMASTER: {
    key: 'DIVEMASTER',
    icon: '⚓',
    title: 'Il percorso da Divemaster',
    headline: 'Sei sulla buona strada per guidare le tue prime immersioni e affiancare i subacquei.',
    unlocks: [
      'Guida gruppi e affianca gli istruttori nelle uscite',
      'Organizza e conduci immersioni per la community',
      'Costruisci la tua reputazione come professionista del mare',
    ],
    requirements: [
      'Un brevetto Divemaster riconosciuto (PADI, SSI, CMAS…)',
      'Il brevetto caricato nel tuo profilo',
    ],
    primaryAction: { label: 'Richiedi il ruolo Divemaster', action: { kind: 'request-role', role: 'DIVEMASTER' } },
    secondaryAction: { label: 'Aggiungi il tuo brevetto', action: { kind: 'add-certification' } },
  },
  INSTRUCTOR: {
    key: 'INSTRUCTOR',
    icon: '🎓',
    title: 'Il percorso da Istruttore',
    headline: 'Un passo importante: formare nuovi subacquei e trasmettere la tua passione per il mare.',
    unlocks: [
      'Crea corsi e forma i tuoi allievi',
      'Rilascia certificazioni e segui i loro progressi',
      'Accedi al pannello istruttore con studenti, classi e materiali',
    ],
    requirements: [
      'Un brevetto da Istruttore riconosciuto (PADI, SSI, CMAS…)',
      'Il brevetto caricato nel tuo profilo',
    ],
    primaryAction: { label: 'Richiedi il ruolo Istruttore', action: { kind: 'request-role', role: 'INSTRUCTOR' } },
    secondaryAction: { label: 'Aggiungi il tuo brevetto', action: { kind: 'add-certification' } },
  },
  DIVE_DOCTOR: {
    key: 'DIVE_DOCTOR',
    icon: '🩺',
    title: 'Il percorso da Medico Subacqueo',
    headline: 'Metti la tua competenza al servizio della sicurezza dei subacquei.',
    unlocks: [
      'Valuta l’idoneità subacquea e apnea dei pazienti',
      'Gestisci referral e certificati medici',
      'Accedi al portale medico dedicato',
    ],
    requirements: [
      'Qualifica di medico abilitato alla medicina subacquea',
      'Verifica del profilo professionale',
    ],
    primaryAction: { label: 'Richiedi il ruolo Medico', action: { kind: 'request-role', role: 'DIVE_DOCTOR' } },
    secondaryAction: { label: 'Completa il profilo professionale', action: { kind: 'complete-profile' } },
  },
  DIVE_CENTER: {
    key: 'DIVE_CENTER',
    icon: '🏝️',
    title: 'Il percorso da Centro Immersioni',
    headline: 'Porta il tuo centro su eDive e raggiungi migliaia di subacquei.',
    unlocks: [
      'Gestisci corsi, uscite, staff e attrezzatura',
      'Ricevi prenotazioni e organizza le tue attività',
      'Fatti trovare dalla community dei subacquei',
    ],
    requirements: [
      'Registra il tuo centro con i dati dell’attività',
    ],
    primaryAction: { label: 'Registra il tuo centro', action: { kind: 'become-center' } },
  },
  TRAVEL_AGENCY: {
    key: 'TRAVEL_AGENCY',
    icon: '✈️',
    title: 'Il percorso da Agenzia Viaggi',
    headline: 'Proponi le tue esperienze di viaggio subacqueo alla community.',
    unlocks: [
      'Pubblica pacchetti, escursioni e liveaboard',
      'Raggiungi subacquei in cerca della prossima avventura',
    ],
    requirements: ['Registra la tua agenzia con i dati dell’attività'],
    primaryAction: { label: 'Registra la tua agenzia', action: { kind: 'request-role', role: 'TRAVEL_AGENCY' } },
  },
  ACADEMY: {
    key: 'ACADEMY',
    icon: '🏫',
    title: 'Il percorso da Academy / Centro Sportivo',
    headline: 'Fai crescere la tua scuola e i tuoi atleti su eDive.',
    unlocks: [
      'Organizza corsi e attività formative',
      'Gestisci soci e programmi',
    ],
    requirements: ['Registra la tua academy con i dati dell’attività'],
    primaryAction: { label: 'Registra la tua academy', action: { kind: 'request-role', role: 'ACADEMY' } },
  },
  ASSOCIATION: {
    key: 'ASSOCIATION',
    icon: '🤝',
    title: 'Il percorso da Associazione',
    headline: 'Unisci la tua associazione alla community e ai progetti di eDive.',
    unlocks: [
      'Gestisci soci, eventi e documenti',
      'Partecipa a progetti di citizen science e tutela del mare',
    ],
    requirements: ['Registra la tua associazione'],
    primaryAction: { label: 'Registra l’associazione', action: { kind: 'request-role', role: 'ASSOCIATION' } },
  },
  UNDERWATER_PHOTOGRAPHER: {
    key: 'UNDERWATER_PHOTOGRAPHER',
    icon: '📸',
    title: 'Il percorso da Fotografo Subacqueo',
    headline: 'Le tue immagini raccontano il mare: falle vedere e riconoscere dalla community.',
    unlocks: [
      'Crea il tuo portfolio e la tua media library',
      'Contribuisci all’enciclopedia delle specie con i tuoi scatti',
      'Fatti riconoscere e proponi le tue foto ai centri e agli eventi',
    ],
    requirements: [
      'Qualche scatto subacqueo da condividere',
      'Un profilo che racconti il tuo stile',
    ],
    primaryAction: { label: 'Attiva il profilo Fotografo', action: { kind: 'set-profile-type', type: 'UNDERWATER_PHOTOGRAPHER' } },
    secondaryAction: { label: 'Completa il tuo profilo', action: { kind: 'complete-profile' } },
  },
  RESEARCH_CENTER: {
    key: 'RESEARCH_CENTER',
    icon: '🔬',
    title: 'Il percorso da Centro di Ricerca',
    headline: 'Trasforma le immersioni della community in conoscenza per il mare.',
    unlocks: [
      'Avvia progetti di citizen science e raccolta dati',
      'Coinvolgi i subacquei nel monitoraggio di specie e siti',
      'Accedi agli strumenti di ricerca e al field journal',
    ],
    requirements: [
      'Registra il tuo centro o ente di ricerca',
      'Descrivi le aree di studio e i progetti',
    ],
    primaryAction: { label: 'Richiedi il ruolo Centro di Ricerca', action: { kind: 'request-role', role: 'RESEARCH_CENTER' } },
    secondaryAction: { label: 'Completa il profilo dell’ente', action: { kind: 'complete-profile' } },
  },
  PROFESSIONAL_DIVER: {
    key: 'PROFESSIONAL_DIVER',
    icon: '🛠️',
    title: 'Il percorso da Professional Diver',
    headline: 'La tua competenza tecnica apre le porte a opportunità di lavoro nel mondo subacqueo.',
    unlocks: [
      'Metti in mostra le tue qualifiche e specializzazioni professionali',
      'Fatti trovare da centri e realtà in cerca di operatori qualificati',
      'Accedi a strumenti e opportunità dedicate ai professionisti',
    ],
    requirements: [
      'Qualifiche professionali subacquee (es. commerciale, scientifico)',
      'Un profilo professionale completo e verificabile',
    ],
    primaryAction: { label: 'Completa il profilo professionale', action: { kind: 'complete-profile' } },
    secondaryAction: { label: 'Aggiungi le tue qualifiche', action: { kind: 'add-certification' } },
  },
}

/** Ritorna il journey per un profileType target, se definito. */
export function getRoleJourney(target: ProfileType): RoleJourney | undefined {
  return ROLE_JOURNEYS[target]
}
